import { useMemo } from 'react';
import styled from 'styled-components';

import { Slide } from '../SlideComponents/slide.types';
import { CSSSlideWrapperViewer } from '../SlideComponents/SlideWrapperViewer';

/**
 * The big "rule" text element in the source slide. We rewrite its `textValue`
 * with the per-objective rule before rendering.
 */
const RULE_TEXT_ELEMENT_ID = '1060764410848';

/**
 * Read-only Phonics Wizard slide preview for SEO landing pages.
 *
 * Renders the canonical "aiAssessedAnswers" slide through the same designer
 * pipeline used elsewhere in the app (CSSSlideWrapperViewer →
 * SlideViewer → SlideElement* + SlateRichTextViewer), with the rule text
 * swapped in per objective. `isAuthenticated={false}` matches the
 * `/live/results` pattern — the slide uses a color background, no CDN
 * image fetch is required.
 */
const PhonicsSlidePreview = ({ rule }: { rule: string }) => {
  const slide = useMemo<Slide>(() => buildSlide(rule), [rule]);

  return (
    <Container>
      <CSSSlideWrapperViewer
        aspect={{ width: 1920, height: 1080 }}
        slide={slide}
        backgroundMedia={slide.backgroundMedia}
        isAuthenticated={false}
        isDiscoverTemplate={false}
        hideLogo
        showStaticPoll={false}
        preventInteraction
        isUsingSockets={false}
      />
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1920 / 1080;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
`;

function buildRuleSlate(rule: string): string {
  return JSON.stringify([
    {
      type: 'paragraph',
      size: 75,
      color: '#FFFFFF',
      align: 'center',
      children: [
        {
          text: `Write as many words as you can using this rule: ${rule}.`,
          color: '#FFFFFF',
        },
      ],
    },
  ]);
}

function buildSlide(rule: string): Slide {
  const ruleTextValue = buildRuleSlate(rule);
  return {
    ...BASE_SLIDE_JSON,
    elements: BASE_SLIDE_JSON.elements.map(el =>
      el.id === RULE_TEXT_ELEMENT_ID ? { ...el, textValue: ruleTextValue } : el,
    ),
  } as Slide;
}

/**
 * Canonical Phonics Wizard "aiAssessedAnswers" slide. Hardcoded so landing
 * pages render without fetching a remote lesson. The rule sentence in
 * element id `1060764410848` is overwritten per objective.
 *
 * The branded background image element (`161061961097`,
 * `mediaFileId: 1404935010321`) is kept intact and resolves through the
 * normal `useImage` → public CDN path. The `phonics-practice` URL root is
 * mapped to a known marketing tenant in
 * `utils/navigator.ts::getDestinationTenantFromUrl`, which is what lets
 * the unauth `x-api-tenant` header on the public file request succeed
 * without a logged-in user.
 */
const BASE_SLIDE_JSON: Slide = ({
  id: '83658841695',
  type: 'aiAssessedAnswers' as Slide['type'],
  width: 1920,
  height: 1080,
  version: 1,
  responseLimit: 1,
  durationSeconds: 60,
  durationSecondsConfig: 60,
  isPremiumContent: true,
  metadata: { language: 'en-US' },
  contextElements: [{ purpose: 'question', elementId: '1060764410848' }],
  backgroundMedia: {
    fileId: '#282F33',
    brightness: 0.49,
    fileMimeType: 'image/color',
    thumbnailSrc: null,
  },
  elements: [
    {
      x: 7.196339320210713e-10,
      y: -5.999995197334215,
      id: '161061961097',
      type: 'image',
      angle: 0,
      width: 1941,
      height: 1086,
      opacity: 1,
      version: 1,
      mediaFileId: '1404935010321',
      borderRadius: 0,
      mediaAltText: 'Image',
      mediaMimetype: 'image/png',
      imageObjectFit: 'fill',
      lockedPosition: false,
      lockedFromDeletion: false,
    },
    {
      x: 96,
      y: 116.2,
      id: '703546977219',
      type: 'shape',
      angle: 0,
      width: 1740,
      colors: ['#000'],
      height: 588,
      opacity: 0.76,
      version: 1,
      borderRadius: 0,
      lockedPosition: false,
      lockedFromDeletion: false,
    },
    {
      x: 96,
      y: 108,
      id: '1060764410848',
      type: 'text',
      angle: 0,
      scale: 1,
      width: 1728,
      height: 236,
      opacity: 1,
      version: 1,
      textValue: buildRuleSlate('-er (/er/) and -est (/est/)'),
      borderRadius: 0,
      lockedPosition: true,
      lockedFromDeletion: true,
    },
    {
      x: 1370,
      y: 1002.8,
      id: '668950828496',
      type: 'shape',
      angle: 0,
      width: 550,
      colors: ['#000'],
      height: 76,
      opacity: 0.76,
      version: 1,
      borderRadius: 0,
      lockedPosition: false,
      lockedFromDeletion: false,
    },
    {
      x: 1387.5,
      y: 1019.4,
      id: '1462335331442',
      type: 'text',
      angle: 0,
      scale: 1,
      width: 471,
      height: 47,
      opacity: 1,
      version: 1,
      textValue:
        '[{"type":"paragraph","children":[{"text":"💡 Earn 1 point per correct word! ","color":"#FFFFFF"}],"align":"right","size":30}]',
      borderRadius: 0,
      lockedPosition: false,
      lockedFromDeletion: false,
    },
  ],
  // Prompts payload isn't read by the static slide viewer.
  prompts: [],
} as unknown) as Slide;

export default PhonicsSlidePreview;
